import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const { language } = useLanguage();
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        type: 'ai',
        content: language === 'fr' 
          ? "Bonjour! Je suis votre assistant commercial pour First Business Group. Comment puis-je vous aider aujourd'hui?"
          : language === 'zh'
          ? "您好！我是First Business Group的商务助手。今天我能为您做些什么？"
          : language === 'ar'
          ? "مرحباً! أنا مساعدك التجاري في First Business Group. كيف يمكنني مساعدتك اليوم؟"
          : language === 'ru'
          ? "Здравствуйте! Я ваш бизнес-помощник в First Business Group. Чем могу помочь сегодня?"
          : language === 'sw'
          ? "Habari! Mimi ni msaidizi wako wa biashara kwa First Business Group. Ninaweza kukusaidia vipi leo?"
          : "Hello! I'm your business assistant for First Business Group. How can I help you today?"
      }]);
    }
  }, [isOpen, language, messages.length]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMsg = { type: 'user', content: inputMessage };
    const currentMessage = inputMessage;
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsLoading(true);

    try {
      console.log('Sending message:', currentMessage);
      const response = await axios.post(`${API}/chat`, {
        message: currentMessage,
        session_id: sessionId,
        language: language
      });

      console.log('Received response:', response.data);

      if (response.data && response.data.response) {
        const aiMsg = { type: 'ai', content: response.data.response };
        console.log('Adding AI message:', aiMsg);
        setMessages(prev => [...prev, aiMsg]);
      } else {
        console.error('No response field in data:', response.data);
        const errorMsg = { 
          type: 'ai', 
          content: language === 'fr' 
            ? "Désolé, je n'ai pas reçu de réponse. Veuillez réessayer."
            : "Sorry, I didn't receive a response. Please try again." 
        };
        setMessages(prev => [...prev, errorMsg]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      console.error('Error details:', error.response?.data || error.message);
      const errorMsg = { 
        type: 'ai', 
        content: language === 'fr' 
          ? "Désolé, une erreur s'est produite. Veuillez réessayer."
          : "Sorry, an error occurred. Please try again." 
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <motion.button
        data-testid="ai-assistant-trigger"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-5 right-5 sm:bottom-8 sm:right-8 h-14 w-14 sm:h-16 sm:w-16 rounded-full text-white shadow-2xl z-50 flex items-center justify-center transition-all duration-300"
        style={{
          background: 'linear-gradient(135deg, #DB242C 0%, #B91C22 100%)',
          boxShadow: '0 4px 20px rgba(219, 36, 44, 0.4)'
        }}
        whileHover={{ scale: 1.1, boxShadow: '0 8px 30px rgba(219, 36, 44, 0.5)' }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-4 left-4 sm:left-auto sm:bottom-28 sm:right-8 w-auto sm:w-[400px] max-w-[calc(100vw-2rem)] h-[70vh] max-h-[600px] sm:h-[600px] glass-card rounded-2xl overflow-hidden z-50 flex flex-col shadow-2xl"
            data-testid="ai-assistant-modal"
          >
            <div className="bg-primary px-4 sm:px-6 py-4 sm:py-6 text-white flex justify-between items-center flex-shrink-0">
              <div className="min-w-0 pr-2">
                <h3 className="font-serif text-lg sm:text-xl font-semibold truncate" data-testid="ai-assistant-title">
                  Business Assistance
                </h3>
                <p className="text-xs sm:text-sm opacity-90">Powered by AI</p>
              </div>
              <button onClick={() => setIsOpen(false)} className="hover:opacity-75 flex-shrink-0" data-testid="ai-assistant-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 chat-container" data-testid="chat-messages-container">
              {messages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    data-testid={msg.type === 'user' ? 'user-message' : 'ai-message'}
                    className={`rounded-2xl p-4 max-w-[80%] ${
                      msg.type === 'user'
                        ? 'bg-muted text-foreground rounded-tr-none'
                        : 'bg-primary/10 text-primary rounded-tl-none border border-primary/10'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </motion.div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-primary/10 text-primary rounded-2xl rounded-tl-none p-4 border border-primary/10">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-3 sm:p-4 border-t border-border flex-shrink-0">
              <div className="flex gap-2">
                <input
                  data-testid="chat-input"
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={language === 'fr' ? 'Tapez votre message...' : 'Type your message...'}
                  className="flex-1 bg-transparent border-b border-primary/20 focus:border-primary px-0 py-3 text-base focus:outline-none transition-colors placeholder:text-muted-foreground/50"
                  disabled={isLoading}
                />
                <button
                  data-testid="send-message-btn"
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className="p-3 rounded-full text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #DB242C 0%, #B91C22 100%)',
                    boxShadow: '0 2px 10px rgba(219, 36, 44, 0.3)'
                  }}
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIAssistant;